import Vue from 'vue';
import VueRouter from 'vue-router';
import MainView from '../views/MainView.vue';
import MarkupsView from '../views/MarkupsView.vue';
import FlutterView from '../views/FlutterView.vue';
import appConfig from '../../appConfig';
import accountRoutes from '@Account/account.routes';
import facilityRoutes, { facilityDocumentRoutes } from '@Facility/facility.routes';
import companyRoutes from '@Company/company.routes';
import feedbackRoutes from '@Feedback/feedback.routes';
import contractorsRoutes from '@Contractors/contractors.routes';
import userRoutes from '@User/user.routes';
import roleRoutes from '@Role/role.routes';
import adminRoutes from '@Admin/admin.routes';
import taskManagerRoutes from '@TaskManager/taskManager.routes';
import taskRoutes from '@Task/task.routes';
import tagsRoutes from '@Tags/tags.routes';
import contractRoutes from '@Contract/contract.routes';
import groupsRoutes from '@Groups/groups.routes';
import dashboardRoutes from '@Dashboard/dashboard.routes';
import registerRoutes from '@Registers/register.routes';
import dataSourcesRoutes from '@/features/DataSources/dataSources.routes';
import releaseNotesRoutes from '@ReleaseNotes/release-notes.routes';
import mastersRoutes from '@/features/Masters/masters.routes';
import taskImportRoutes from '@/features/TaskImport/taskImport.routes';
import documentImportsRoutes from '@/features/DocumentImports/documentImports.routes';
import potreeMeasurementsRoutes from '@/features/PotreeMeasurements/potree-measurements.routes';
import verificationRoutes from '@/features/Verification/verification.routes';
import EulaView from '../views/EulaView.vue';

import store from '@Store';
import { $has } from '@Utils';
import { isRoutePermitted } from './routePermissions';
import { cancelPendingViewerWaits } from '@/utils/viewerNavigationUtils';
import { isViewerRoute } from '@/constants/navigationTypes';
import httpService from '@Utils/httpService';
import { facilityChangedBetween, pageChangedBetween } from '@/utils/http/navigationScope';
import Chatbot from '@/features/Chatbot/components/chatbot-modal.vue';
Vue.use(VueRouter);

const routes = [
  ...potreeMeasurementsRoutes,  // Must be first to avoid conflicts with /:id/:taskTypeId
  ...facilityDocumentRoutes,    // Must come before taskRoutes: /:id/:taskTypeId would take /:id/document/:documentId
  ...verificationRoutes,        // Must come before taskRoutes to avoid /:id/:taskTypeId interception of /:id/verification/:taskId
  ...adminRoutes,
  ...accountRoutes,
  ...companyRoutes,
  ...feedbackRoutes,
  ...releaseNotesRoutes,
  ...contractorsRoutes,
  ...userRoutes,
  ...roleRoutes,
  ...taskManagerRoutes,
  ...contractRoutes,
  ...groupsRoutes,

  ...dashboardRoutes,

  ...registerRoutes,
  ...dataSourcesRoutes,
  ...mastersRoutes,
  ...taskImportRoutes,
  ...documentImportsRoutes,
  ...taskRoutes,
  ...tagsRoutes,
  {
    path: '/viewLocal.html',
    name: 'viewLocal.html',
    component: MainView,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/chatbot',
    name: 'chatbot',
    component: Chatbot,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/markups',
    name: 'MarkupsView',
    component: MarkupsView,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/markups.html',
    name: 'MarkupsView.html',
    component: MarkupsView,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/markupsLocal.html',
    name: 'MarkupsViewLocal.html',
    component: MarkupsView,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/flutter',
    name: 'FlutterView',
    component: FlutterView,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/flutterView.html',
    name: 'FlutterView.html',
    component: FlutterView,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/access-denied',
    name: 'error-not-authorize',
    component: () => import('../views/ErrorNotAuthorize.vue'),
    meta: {
      requiresAuth: true,
      title: 'Access Denied',
    },
  },
  {
    path: '/eula',
    name: 'eula',
    component: EulaView,
    meta: {
      layout: 'auth-layout',
      requiresAuth: true,
      title: 'User Agreement',
    },
  },
  ...facilityRoutes,
];

const router = new VueRouter({
  mode: 'history',
  base: appConfig.appRoot,
  routes,
});
router.beforeEach(async (to, from, next) => {
  // Clean up pending viewer watchers when navigating away from viewer routes
  if (isViewerRoute(from.name) && !isViewerRoute(to.name)) {
    cancelPendingViewerWaits();
  }

  // Cancel the requests this navigation leaves behind: the page's own requests when it leaves the
  // page (a query-only change such as a dashboard's ?tab= keeps them), the
  // facility-wide loads (requestScope 'facility') only when leaving that facility. Cancelling the
  // facility loads on e.g. Integrity Hub -> viewer left the scans and point clouds empty.
  httpService.cancelOnNavigation({
    facilityChanged: facilityChangedBetween(from, to),
    pageChanged: pageChangedBetween(from, to),
  });

  const isAuth = store.getters['ACCOUNT/GET_IS_AUTH'];
  const isEulaAccepted = store.getters['ACCOUNT/GET_IS_EULA_ACCEPTED'];

  if (to.meta.hidden) return next({ name: 'error-not-authorize', query: { redirected: to.path } });

  if (to.meta.requiresAuth && !isAuth) {
    // Routes reached from outside the app (e.g. the AI-assistant consent link) come back here after sign-in.
    return next(to.meta.returnAfterLogin ? { path: '/login', query: { redirect: to.fullPath } } : '/login');
  }

  if (isAuth && !isEulaAccepted && to.path !== '/eula') {
    return next({ path: '/eula', query: { redirect: to.fullPath } });
  }
  if (isAuth && isEulaAccepted && to.path === '/eula') {
    const redirectTo = to.query.redirect || '/';
    return next(redirectTo);
  }
  // Facility access guard: any facility-scoped route (/:id/...) must belong to
  // a facility the user can actually access. Covers pasted deep links and
  // share-intent URLs before MainView mounts or any intent decodes.
  // The 5-min FETCH_ACCESSIBLE cache keeps repeat navigations at ~0ms.
  const isFacilityScoped = to.matched.some((r) => r.meta && r.meta.facilityScope);
  if (isAuth && isFacilityScoped && to.params.id && to.name !== 'error-not-authorize') {
    const allowed = await store.dispatch('FACILITY/CHECK_ACCESS', to.params.id);
    if (!allowed) {
      return next({ name: 'error-not-authorize', query: { redirected: to.fullPath } });
    }
  }

  if (isAuth && !isRoutePermitted(to, $has)) {
    return next({ name: 'error-not-authorize', query: { redirected: to.path } });
  }
  if (isAuth && to.path == '/login') return next('/');

  next();
});
router.afterEach((to, from) => {
  // change page title
  if (to.meta && to.meta.title) {
    document.querySelector('title').innerText = `Envizion - ${to.meta.title}`;
  } else {
    document.querySelector('title').innerText = `Envizion`;
  }

  if (to.matched.some((record) => record.meta && record.meta.layout === 'viewer-layout')) {
    document.body.classList.add('viewer-layout');
  } else {
    document.body.classList.remove('viewer-layout');
  }

  // ─────────────────────────────────────────────────────────────
  // Smart Navigation History Integration
  // Records navigations for "Back to [Previous]" functionality
  //
  // IMPORTANT: We delegate the programmatic navigation check to the
  // RECORD_NAVIGATION action itself rather than checking here. This
  // avoids race conditions where state reads in afterEach happen before
  // Vuex mutations are processed.
  //
  // CRITICAL (Issue #7): The router.replace() to clean __navId triggers
  // a SECOND afterEach. We must keep activeNavigationId set until AFTER
  // that second hook fires, so we clear it in the .then() callback.
  // ─────────────────────────────────────────────────────────────

  // Log for debugging (filter by "[NavHistory]" in console)
  if (from.name || to.params?.id) {
    console.log('[NavHistory] Router afterEach:', {
      from: from.name,
      to: to.name,
      fromPath: from.fullPath,
      toPath: to.fullPath,
      hasFacilityId: !!to.params?.id,
      hasNavId: !!to.query?.__navId
    });
  }

  // Check for programmatic navigation marker in query
  const navId = to.query?.__navId;

  // Always dispatch - action checks activeNavigationId internally
  // This avoids race conditions with state read timing
  store.dispatch('NAVIGATION_HISTORY/RECORD_NAVIGATION', { from, to });

  // Clean up navId from URL if present
  if (navId) {
    // CRITICAL: Do NOT clear activeNavigationId here!
    // The router.replace() below triggers another afterEach
    // That second afterEach also needs to skip RECORD_NAVIGATION
    const cleanQuery = { ...to.query };
    delete cleanQuery.__navId;

    router.replace({ ...to, query: cleanQuery })
      .then(() => {
        // Clear AFTER replace completes and its afterEach has fired
        // At this point, both afterEach hooks have completed
        store.commit('NAVIGATION_HISTORY/CLEAR_ACTIVE_NAVIGATION', navId);
      })
      .catch(() => {
        // Clear on error too (navigation might have been cancelled)
        store.commit('NAVIGATION_HISTORY/CLEAR_ACTIVE_NAVIGATION', navId);
      });
  }
});
export default router;
